import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import styles from "./Tabs.module.css";
import { cx } from "./cx.js";

export interface TabOption<T extends string> {
  value: T;
  label: string;
}

interface TabsProps<T extends string> {
  /** Names the set for a screen reader: "Transaction", not "Tabs". */
  label: string;
  tabs: Array<TabOption<T>>;
  value: T;
  onChange: (value: T) => void;
  children: ReactNode;
}

/**
 * One panel at a time, with the arrow keys wired up.
 *
 * The tablist and the panel are one component because the ids that tie them
 * together — `aria-controls` one way, `aria-labelledby` the other — have to be
 * generated somewhere, and a caller made to thread them through is a caller
 * that will eventually get them wrong.
 *
 * Selection follows focus, which is the expected behaviour when the panels are
 * already loaded and cheap to swap between.
 */
export function Tabs<T extends string>({ label, tabs, value, onChange, children }: TabsProps<T>) {
  const base = useId();
  const panelId = `${base}-panel`;
  const tabId = (tab: string) => `${base}-tab-${tab}`;
  const buttons = useRef(new Map<string, HTMLButtonElement>());

  function moveFocus(event: KeyboardEvent<HTMLDivElement>) {
    const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1 };
    const offset = offsets[event.key];

    const current = tabs.findIndex((tab) => tab.value === value);
    const next =
      event.key === "Home"
        ? tabs[0]
        : event.key === "End"
          ? tabs.at(-1)
          : offset === undefined
            ? undefined
            : tabs[(current + offset + tabs.length) % tabs.length];

    if (!next) return;
    event.preventDefault();
    onChange(next.value);
    buttons.current.get(next.value)?.focus();
  }

  return (
    <div className={styles.tabs}>
      <div role="tablist" aria-label={label} className={styles.list} onKeyDown={moveFocus}>
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            id={tabId(tab.value)}
            ref={(node) => {
              if (node) buttons.current.set(tab.value, node);
              else buttons.current.delete(tab.value);
            }}
            aria-selected={tab.value === value}
            aria-controls={panelId}
            // Only the selected tab is a tab stop; the arrows move between them.
            tabIndex={tab.value === value ? 0 : -1}
            className={cx(styles.tab, tab.value === value && styles.selected)}
            onClick={() => onChange(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={panelId} aria-labelledby={tabId(value)} className={styles.panel}>
        {children}
      </div>
    </div>
  );
}
